import * as React from 'react';
import { memo, useMemo, useRef } from 'react';
import { FormState, Mutator } from 'final-form';
import { Form as FinalForm, useFormState } from 'react-final-form';

import Contacts from './Contacts/Contacts';
import Passengers from './Passengers/Passengers';
import { FormValues, Passenger, Step } from '../../../types';
import PassengersToolbar from './PassengersToolbar/PassengersToolbar';
import { GetAccountInfoQuery, CheckoutOrderFragment, CustomerFragment } from '@websky/graphql';
import scrollToFirstErrorDecorator from './scrollToFirstErrorDecorator';
import { OrderSubsidyState } from './SubsidyRequest/subsidyHook';
import { scrollTo } from '../../../../utils';
import { getIsPriceFreezeProductSelected } from '../../../store/order/selectors';
import { useSelector } from 'react-redux';
import Login from './Login/Login';
import { usePassengerForm } from '../../../../PassengerForm/hooks';
import FormStateWrapper from './FormStateWrapper/FormStateWrapper';
import CheckDataModal from './CheckDataModal/CheckDataModal';
import FormValidationTrigger from './FormValidationTrigger';
import { clearFieldsMutator, fillFromDocumentMutator } from './utils';

export interface Props {
	passengerClassName?: string;
	currentStep: Step;
	showContacts?: boolean;
	showForm?: boolean;
	showServices?: boolean;
	showToolbar?: boolean;
	customer: CheckoutOrderFragment['customer'];
	passengers: Passenger[];
	onFormSubmit: (values: object) => Promise<CheckoutOrderFragment | void>;
	isInternationalFlight?: boolean;
	isChangePassengers?: boolean;
	userInfo?: GetAccountInfoQuery['CurrentUser'];
	refetchUser?: () => Promise<GetAccountInfoQuery>;
	inModal?: boolean;
	orderSubsidyState?: OrderSubsidyState;
	backToResults?: () => void;
	setNextStep: () => void;
	passengersFormFirst?: boolean;
	isChangeFiles?: boolean;
	hideClearButton?: boolean;
	allowSwapNames?: boolean;
	showCheckDataModal?: boolean;
	onToggleChangeFiles?: (expanded: boolean) => void;
	onChangeFormSubscription?: (values: FormValues) => void;
	renderToolbar?: (state: FormState<object>, formRef?: React.RefObject<HTMLFormElement>) => React.ReactElement;
	autoValidate?: boolean;
}

export const Form: React.FC<Props> = memo(
	({
		passengerClassName,
		currentStep,
		passengers,
		customer,
		onFormSubmit,
		isInternationalFlight,
		showForm = true,
		showContacts = true,
		showServices = true,
		showToolbar = true,
		userInfo,
		refetchUser,
		inModal = false,
		isChangePassengers,
		allowSwapNames,
		orderSubsidyState,
		backToResults,
		setNextStep,
		passengersFormFirst,
		showCheckDataModal,
		renderToolbar,
		isChangeFiles,
		onToggleChangeFiles,
		onChangeFormSubscription,
		hideClearButton,
		autoValidate
	}) => {
		const formRef = useRef<HTMLFormElement>();

		const isFreezeOrder = useSelector(getIsPriceFreezeProductSelected);

		React.useEffect(() => {
			scrollTo(0);
		}, []);

		const {
			state: initialValues,
			selectedProfileIdsByPassenger,
			isCheckFfpStatusLoading,
			isCheckDataModalOpen,
			onFillValues,
			onSwapNames,
			onConfirm,
			onModalConfirm,
			closeCheckDataModal
		} = usePassengerForm(
			passengers,
			onFormSubmit,
			customer as CustomerFragment,
			isInternationalFlight,
			isFreezeOrder,
			showCheckDataModal
		);

		const mutators = useMemo(
			() => ({
				fillFromDocument: fillFromDocumentMutator,
				clearFields: clearFieldsMutator(passengers)
			}),
			[passengers]
		);

		return (
			<>
				<FinalForm<FormValues>
					onSubmit={onConfirm}
					decorators={[scrollToFirstErrorDecorator]}
					mutators={mutators as { [key: string]: Mutator }}
					validateOnBlur={true}
					initialValues={initialValues}
					keepDirtyOnReinitialize={true}
					subscription={{ submitErrors: true }}
				>
					{({ handleSubmit }) => (
						<Login refetchUser={refetchUser} userInfo={userInfo}>
							{({ userInfo, refetchUser, openLogin, isLoginFormOpen }) => (
								<form ref={formRef} onSubmit={handleSubmit} noValidate>
									<FormValidationTrigger enabled={autoValidate} />

									{showContacts && (
										<Contacts
											userInfo={userInfo}
											refetchUser={refetchUser}
											submitForm={handleSubmit}
											customer={customer}
											onLoginOpen={openLogin}
										/>
									)}

									{showForm && (
										<Passengers
											hideClearButton={hideClearButton}
											className={passengerClassName}
											isAuthorized={!!userInfo?.userProfile}
											isChangePassengers={isChangePassengers}
											onFill={onFillValues}
											passengers={passengers}
											orderSubsidies={orderSubsidyState}
											backToResults={backToResults}
											selectedProfileIdsByPassenger={selectedProfileIdsByPassenger}
											showLoginForm={() => openLogin('')}
											userInfo={userInfo}
											refetchUser={refetchUser}
											isLoginFormOpen={isLoginFormOpen}
											isChangeFiles={isChangeFiles}
											onSwap={allowSwapNames ? onSwapNames : null}
											onToggleChangeFiles={onToggleChangeFiles}
											onChangeFormSubscription={onChangeFormSubscription}
										/>
									)}

									{showToolbar && (
										<>
											{renderToolbar ? (
												<FormStateWrapper formRef={formRef}>{renderToolbar}</FormStateWrapper>
											) : (
												<PassengersToolbar
													disableBooking={orderSubsidyState?.isSubsidiesRejected}
													formRef={formRef}
													currentStep={currentStep}
													loading={orderSubsidyState?.isLoading || isCheckFfpStatusLoading}
													setNextStep={setNextStep}
													formState={useFormState()}
													hasForm
												/>
											)}
										</>
									)}
								</form>
							)}
						</Login>
					)}
				</FinalForm>

				{showCheckDataModal && (
					<CheckDataModal
						isOpen={isCheckDataModalOpen}
						onClose={closeCheckDataModal}
						onConfirm={onModalConfirm}
						onCancel={closeCheckDataModal}
					/>
				)}
			</>
		);
	}
);
